import * as THREE from 'three';
import { withFrozenBones } from '../entities/volume-skin.js';

interface Bounds {
  geometry: THREE.BufferGeometry;
  boxes: Map<number, THREE.Box3>;
  supported: boolean;
}
const bounds = new WeakMap<THREE.SkinnedMesh, Bounds>();

function build(mesh: THREE.SkinnedMesh): Bounds {
  const geometry = mesh.geometry, boxes = new Map<number, THREE.Box3>();
  // Nonlinear volume skinning and morphs need their own conservative envelope.
  let supported = !geometry.userData.nimblezAnimated && !geometry.hasAttribute('skinVolume') && !Object.keys(geometry.morphAttributes).length;
  if (supported) {
    const positions = geometry.getAttribute('position'), indices = geometry.getAttribute('skinIndex'), weights = geometry.getAttribute('skinWeight');
    const point = new THREE.Vector3(), local = new THREE.Vector3();
    for (let v = 0; v < positions.count; v++) {
      point.fromBufferAttribute(positions, v).applyMatrix4(mesh.bindMatrix);
      let total = 0;
      for (let c = 0; c < 4; c++) {
        const weight = weights.getComponent(v, c); total += weight;
        if (weight < 0) supported = false;
        if (!weight) continue;
        const bone = indices.getComponent(v, c), box = boxes.get(bone) ?? new THREE.Box3();
        local.copy(point).applyMatrix4(mesh.skeleton.boneInverses[bone]); box.expandByPoint(local); boxes.set(bone, box);
      }
      if (Math.abs(total - 1) > 1e-4) supported = false;
    }
  }
  const result = { geometry, boxes, supported }; bounds.set(mesh, result); return result;
}

/** Build during figure preparation, rather than on the first arrow impact. */
export function prepareImpactBounds(root: THREE.Object3D) {
  root.traverse(object => { if (object instanceof THREE.SkinnedMesh) { build(object); capsulesOf(object); } });
}

/** A capsule in a bone's own space: the segment a–b and the radius around it. */
interface Capsule { bone: number; a: THREE.Vector3; b: THREE.Vector3; radius: number }
const capsules = new WeakMap<THREE.SkinnedMesh, Capsule[]>();
/** Fewer vertices than this on a bone (a finger joint, an eyelid) make no capsule; their neighbours' cover them. */
const FEWEST = 8;
/**
 * Each bone's part of a skinned surface as a capsule, fitted once to the vertices the bone mostly carries, in its bind pose: along their
 * main axis, trimmed to the middle 80 %, wide enough to reach half of them. Arrows lodge on these, a few centimetres from the
 * drawn skin at most, instead of posing every vertex of the body for every hit.
 */
function capsulesOf(mesh: THREE.SkinnedMesh): Capsule[] {
  const known = capsules.get(mesh);
  if (known) return known;
  const geometry = mesh.geometry, positions = geometry.getAttribute('position'), indices = geometry.getAttribute('skinIndex'), weights = geometry.getAttribute('skinWeight');
  const result: Capsule[] = [];
  capsules.set(mesh, result);
  if (!positions || !indices || !weights) return result;
  const byBone = new Map<number, number[]>(), point = new THREE.Vector3();
  for (let v = 0; v < positions.count; v++) for (let c = 0; c < 4; c++) {
    if (weights.getComponent(v, c) < .5) continue;
    const bone = indices.getComponent(v, c), inverse = mesh.skeleton.boneInverses[bone]; if (!inverse) continue;
    point.fromBufferAttribute(positions, v).applyMatrix4(mesh.bindMatrix).applyMatrix4(inverse);
    const list = byBone.get(bone) ?? []; list.push(point.x, point.y, point.z); byBone.set(bone, list);
  }
  for (const [bone, list] of byBone) {
    const count = list.length / 3; if (count < FEWEST) continue;
    const centre = new THREE.Vector3();
    for (let i = 0; i < list.length; i += 3) centre.x += list[i], centre.y += list[i + 1], centre.z += list[i + 2];
    centre.divideScalar(count);
    // The main axis: power iteration on the points' spread.
    let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
    for (let i = 0; i < list.length; i += 3) {
      const x = list[i] - centre.x, y = list[i + 1] - centre.y, z = list[i + 2] - centre.z;
      xx += x * x; xy += x * y; xz += x * z; yy += y * y; yz += y * z; zz += z * z;
    }
    const axis = new THREE.Vector3(1, 1, 1).normalize(), next = new THREE.Vector3();
    for (let k = 0; k < 24; k++) { next.set(xx * axis.x + xy * axis.y + xz * axis.z, xy * axis.x + yy * axis.y + yz * axis.z, xz * axis.x + yz * axis.y + zz * axis.z); if (next.lengthSq() < 1e-20) break; axis.copy(next).normalize(); }
    const along: number[] = [];
    for (let i = 0; i < list.length; i += 3) along.push((list[i] - centre.x) * axis.x + (list[i + 1] - centre.y) * axis.y + (list[i + 2] - centre.z) * axis.z);
    along.sort((p, q) => p - q);
    const low = along[Math.floor(count * .1)], high = along[Math.floor(count * .9)];
    const a = centre.clone().addScaledVector(axis, low), b = centre.clone().addScaledVector(axis, high), segment = new THREE.Line3(a, b), closest = new THREE.Vector3();
    const reach: number[] = [];
    for (let i = 0; i < list.length; i += 3) reach.push(segment.closestPointToPoint(point.set(list[i], list[i + 1], list[i + 2]), true, closest).distanceTo(point));
    reach.sort((p, q) => p - q);
    result.push({ bone, a, b, radius: reach[Math.floor(count * .5)] });
  }
  return result;
}

const capsuleMatrix = new THREE.Matrix4(), capsuleA = new THREE.Vector3(), capsuleB = new THREE.Vector3(), scaleX = new THREE.Vector3(), scaleY = new THREE.Vector3(), scaleZ = new THREE.Vector3();
/** Where `ray` first enters this surface's bone capsules, as a distance along it, or Infinity. */
export function capsuleHit(mesh: THREE.SkinnedMesh, ray: THREE.Ray, far = Infinity) {
  let best = Infinity;
  for (const capsule of capsulesOf(mesh)) {
    const bone = mesh.skeleton.bones[capsule.bone]; if (!bone) continue;
    // Posed as the vertices are: the mesh's world matrix, unbound, then the bone.
    capsuleMatrix.multiplyMatrices(mesh.matrixWorld, mesh.bindMatrixInverse).multiply(bone.matrixWorld);
    capsuleMatrix.extractBasis(scaleX, scaleY, scaleZ);
    const t = rayCapsule(ray, capsuleA.copy(capsule.a).applyMatrix4(capsuleMatrix), capsuleB.copy(capsule.b).applyMatrix4(capsuleMatrix),
      capsule.radius * Math.max(scaleX.length(), scaleY.length(), scaleZ.length()));
    if (t >= 0 && t <= far && t < best) best = t;
  }
  return best;
}

/** Distance along `ray` (unit direction) to where it enters the capsule a–b of radius r, or -1 for a miss. */
function rayCapsule(ray: THREE.Ray, a: THREE.Vector3, b: THREE.Vector3, r: number) {
  const ro = ray.origin, rd = ray.direction;
  const bax = b.x - a.x, bay = b.y - a.y, baz = b.z - a.z, oax = ro.x - a.x, oay = ro.y - a.y, oaz = ro.z - a.z;
  const baba = bax * bax + bay * bay + baz * baz, bard = bax * rd.x + bay * rd.y + baz * rd.z, baoa = bax * oax + bay * oay + baz * oaz;
  const rdoa = rd.x * oax + rd.y * oay + rd.z * oaz, oaoa = oax * oax + oay * oay + oaz * oaz;
  const qa = baba - bard * bard, qb = baba * rdoa - baoa * bard, qc = baba * oaoa - baoa * baoa - r * r * baba;
  let h = qb * qb - qa * qc;
  if (h >= 0 && qa > 1e-12) {
    const t = (-qb - Math.sqrt(h)) / qa, y = baoa + t * bard;
    if (y > 0 && y < baba) return t;
  }
  // The end caps: the sphere at whichever end the ray meets the cylinder's extension past.
  let best = -1;
  for (const end of [a, b]) {
    const ox = ro.x - end.x, oy = ro.y - end.y, oz = ro.z - end.z, cb = rd.x * ox + rd.y * oy + rd.z * oz, cc = ox * ox + oy * oy + oz * oz - r * r;
    h = cb * cb - cc;
    if (h < 0) continue;
    const t = -cb - Math.sqrt(h);
    if (t >= 0 && (best < 0 || t < best)) best = t;
  }
  return best;
}

const world = new THREE.Box3(), transformed = new THREE.Box3(), matrix = new THREE.Matrix4(), base = new THREE.Matrix4();
/** Linear skin weights form convex combinations, so the union box contains every posed triangle. */
export function mayHitSkin(mesh: THREE.SkinnedMesh, ray: THREE.Ray): boolean {
  // Settled corpse instances retain animator references but now use a baked Mesh surface.
  if (!mesh.isSkinnedMesh) return true;
  let entry = bounds.get(mesh);
  if (!entry || entry.geometry !== mesh.geometry) entry = build(mesh);
  if (!entry.supported) return true;
  world.makeEmpty(); base.multiplyMatrices(mesh.matrixWorld, mesh.bindMatrixInverse);
  for (const [bone, box] of entry.boxes) {
    matrix.multiplyMatrices(base, mesh.skeleton.bones[bone].matrixWorld);
    world.union(transformed.copy(box).applyMatrix4(matrix));
  }
  world.expandByScalar(1e-4);
  return ray.intersectsBox(world);
}

interface PosedVertices { geometry: THREE.BufferGeometry; positions: Float64Array; seen: Uint32Array; query: number }
const posed = new WeakMap<THREE.SkinnedMesh, PosedVertices>();
/** Indexed triangles reuse each posed vertex within this synchronous query, including nonlinear skin. */
export function intersectImpactSurface(object: THREE.Object3D, ray: THREE.Raycaster, hits: THREE.Intersection[]) {
  if (!(object instanceof THREE.SkinnedMesh) || !object.geometry.index) { ray.intersectObject(object, false, hits); return; }
  const count = object.geometry.getAttribute('position').count;
  let cache = posed.get(object);
  if (!cache || cache.geometry !== object.geometry || cache.seen.length !== count) {
    cache = { geometry: object.geometry, positions: new Float64Array(count * 3), seen: new Uint32Array(count), query: 0 }; posed.set(object, cache);
  }
  cache.query = (cache.query + 1) >>> 0;
  if (!cache.query) { cache.seen.fill(0); cache.query = 1; }
  const original = object.getVertexPosition, current = cache;
  object.getVertexPosition = function(index, target) {
    if (current.seen[index] !== current.query) {
      original.call(this, index, target); target.toArray(current.positions, index * 3); current.seen[index] = current.query;
    } else target.fromArray(current.positions, index * 3);
    return target;
  };
  try { withFrozenBones([object], () => ray.intersectObject(object, false, hits)); }
  finally { object.getVertexPosition = original; }
}
